import { useCoverage } from './CoverageContext';
import { SourceIdentifier } from '@ais-ui/api-spec/coverage';
import { CoverageImageWMSLayer } from './CoverageImageLayer';
import { memo, useMemo } from 'react';

interface SingleCoverageLayerProps {
  sourceIdentifier: SourceIdentifier;
}

const SingleCoverageLayer = memo(
  ({ sourceIdentifier }: SingleCoverageLayerProps) => {
    const { coverageOverlays } = useCoverage();

    const properties = useMemo(() => {
      const extent = coverageOverlays[sourceIdentifier.id]?.imageExtent;

      // Get the source identifier position directly for zoom functionality
      const sourceIdentifierPos = sourceIdentifier.pos;

      return {
        id: sourceIdentifier.id,
        type: 'coverage', // Add the layer type for LayerControl identification
        name: `coverage ${
          sourceIdentifier.pssMmsi !== -1
            ? sourceIdentifier.pssMmsi
            : sourceIdentifier.sourceTag
        }`,
        pssMmsi: sourceIdentifier.pssMmsi,
        sourceTag: sourceIdentifier.sourceTag,
        extent: extent,
        // Use the source identifier position directly - no need to calculate center
        sourceIdentifierPos: sourceIdentifierPos,
      };
    }, [coverageOverlays, sourceIdentifier]);

    if (!coverageOverlays[sourceIdentifier.id]) {
      return null;
    }

    return (
      <CoverageImageWMSLayer
        key={sourceIdentifier.id}
        overlay={coverageOverlays[sourceIdentifier.id].base64}
        extent={coverageOverlays[sourceIdentifier.id].imageExtent}
        name={properties.name}
        properties={properties}
      />
    );
  },
);

SingleCoverageLayer.displayName = 'SingleCoverageLayer';

export const CoverageLayer = memo(() => {
  const { selectedSourceIdentifiers } = useCoverage();

  return (
    <>
      {selectedSourceIdentifiers.map((sourceIdentifier) => {
        return (
          <SingleCoverageLayer
            key={sourceIdentifier.id}
            sourceIdentifier={sourceIdentifier}
          />
        );
      })}
    </>
  );
});

CoverageLayer.displayName = 'CoverageLayer';
